import { Box } from '@cdl/heartbeat-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { roundedDurationSelector } from '../../ecg/selectors/ecg.selectors';
import { StripContent } from '../../plot/components/StripContent';
import { useStrip } from '../../plot/hooks/useStrip';
import { Scale } from '../../plot/types/scale';
import { usePointColor } from '../hooks/usePointColor';
import { getScatterContent } from '../../plot/utils/common-graph.utils';
import {
  scatterDotsRadiusSelector,
  scatterPointsSelector,
  useParentOnsetsSelector,
} from '../selectors/scatter.selectors';

interface Props {
  stripStart: number;
  yScale: Scale;
}

export const ScatterGraph: React.FC<Props> = ({ stripStart, yScale }) => {
  const scatterPoints = useSelector(scatterPointsSelector);
  const duration = useSelector(roundedDurationSelector);
  const showPositionLine = useSelector(useParentOnsetsSelector);
  const { xScale, height } = useStrip();
  const computePointColor = usePointColor();
  const size = useSelector(scatterDotsRadiusSelector);

  const points = scatterPoints.map((point) => ({
    x: xScale(point.timestamp),
    y: yScale(point.bpm),
    color: computePointColor(point.timestamp),
    wave: point.wave,
    bordered: false,
  }));

  return (
    <g className='scatter-graph'>
      <StripContent>
        {points.map((point, index) => {
          const content = getScatterContent(point.wave, size, point.color);
          return (
            <g
              key={index}
              transform={`translate(${point.x}, ${point.y})`}
              data-testid='scatter-point'
            >
              {content}
            </g>
          );
        })}
        {showPositionLine && (
          <Box
            as='rect'
            x={xScale(stripStart)}
            y={0}
            width={xScale(duration) - xScale(0)}
            height={height}
            fill='primary.500'
            fillOpacity={0.35}
            strokeWidth={3}
          />
        )}
      </StripContent>
    </g>
  );
};
